import React, { CSSProperties, useCallback, useMemo, useState } from 'react';
import { IToggleButton } from '../../../models/toggle-button/IToggleButton';
import ToggleButton from '../../atoms/toggle-button/toggle-button';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import breakpoints from '../../../styles/partials/breakpoints.module.scss';
import colorStyle from '../../../styles/utils/variables.module.scss';
import styles from './toggle-button-group.module.scss';
import { deviceDetect } from '../../../data/device-detect';

type Props = {
  toggleButtonProps: IToggleButton[];
  heapIdentifier?: string;
  customClass?: string;
};

export default function ToggleButtonGroup({
  toggleButtonProps,
  heapIdentifier,
  customClass = ``,
}: Props) {
  const [isBreakpointTabletUp, setIsBreakpointTabletUp] = useState(!deviceDetect.isMobile);

  useWindowResize(
    (newWidth) => setIsBreakpointTabletUp(newWidth > parseInt(breakpoints.tablet_landscape, 10)),
    100,
  );

  const [isOpen, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const toggleDropdown = useCallback(() => setOpen(!isOpen), [isOpen]);

  const handleItemClick = useCallback(
    (value) => {
      selectedItem == value ? setSelectedItem(null) : setSelectedItem(value);
      toggleDropdown();
    },
    [selectedItem],
  );

  const renderData = useMemo(() => {
    const inputColor = toggleButtonProps.find((item) => item.isChecked).inputColor;

    const toggleTriggerStyle: CSSProperties = {
      borderColor: inputColor ? inputColor : colorStyle.primary,
      backgroundColor: inputColor ? inputColor : colorStyle.primary,
    };

    return { toggleTriggerStyle };
  }, [toggleButtonProps]);

  return (
    <div className={`${styles[`toggle-group`]}`}>
      {isBreakpointTabletUp &&
        toggleButtonProps.map((item) => (
          <ToggleButton
            inputColor={item.inputColor}
            key={item.label}
            label={item.label}
            value={item.value}
            group={item.group}
            handleOnToggle={item.handleOnToggle}
            isChecked={item.isChecked}
            heapIdentifier={heapIdentifier}
            customClass={customClass}
          />
        ))}

      {!isBreakpointTabletUp && (
        <div className={`${styles[`toggle-dropdown`]}`}>
          <div
            className={`${styles[`toggle-dropdown-trigger`]} ${isOpen && styles[`open`]}`}
            onClick={toggleDropdown}
            style={renderData.toggleTriggerStyle}
          >
            {selectedItem
              ? toggleButtonProps.find((item) => item.value == selectedItem).label
              : toggleButtonProps.find((item) => item.isChecked).label}
            <i className={`${styles[`toggle-dropdown-arrow`]} ${isOpen && styles[`open`]}`}></i>
          </div>
          <div className={`${styles[`toggle-dropdown-body`]} ${isOpen && styles[`open`]}`}>
            {toggleButtonProps.map((item) => (
              <ToggleButton
                key={item.label}
                inputColor={item.inputColor}
                label={item.label}
                value={item.value}
                group={item.group}
                handleOnToggle={item.handleOnToggle}
                handleOnClick={(e) => handleItemClick(item.value)}
                isChecked={item.isChecked}
                heapIdentifier={heapIdentifier}
                customClass='toggle-dropdown-item'
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
